import { useState } from "react";
import { API } from "../services/api";
import { formatChronicDescription, markChronicProductInfoBilled, parseChronicDescription } from "../utils/chronicBilling";

const NEVER_FROZEN = () => false;

// Lignes de facturation chronique d'un patient : motif, commentaire, passage en facturé et retrait.
// Commun à l'Administration et aux écrans de contrôle ; chaque écran fournit :
// - reload(patientId) pour recharger la fiche après une modification,
// - apiConfig (options axios de l'écran),
// - resolveMedicamentId(patientId, productInfo) pour une ligne qui ne porte pas l'identifiant du médicament,
// - isFrozen(patientId) quand la fiche ne doit plus être modifiée.
export function useChronicBillingLines({ user, reload, apiConfig, resolveMedicamentId, isFrozen = NEVER_FROZEN, onError }) {
    const [savingId, setSavingId] = useState(null);
    const [deleteCandidate, setDeleteCandidate] = useState(null);
    const [billCandidate, setBillCandidate] = useState(null);
    const [historyPatient, setHistoryPatient] = useState(null);

    const putLine = (billingEvent, description) => API.put(`/administration/billing-events/${billingEvent.id}`, {
        type: "CHRONIC",
        date: billingEvent.date,
        description,
    }, apiConfig);

    const runOnLine = async (patient, billingEvent, action, fallbackError) => {
        setSavingId(billingEvent.id);
        onError("");
        try {
            await action();
            await reload(patient.patientId);
        } catch (requestError) {
            onError(requestError.response?.data?.message || fallbackError);
        } finally {
            setSavingId(null);
        }
    };

    const canEdit = (patient, billingEvent) => Boolean(patient?.patientId && billingEvent?.id && !isFrozen(patient.patientId));

    const updateReason = async (patient, billingEvent, reason) => {
        if (!canEdit(patient, billingEvent)) return;
        const info = parseChronicDescription(billingEvent.description);
        await runOnLine(patient, billingEvent, () => putLine(
            billingEvent,
            formatChronicDescription(info.productInfo, reason, info.medicamentId, info.comment)
        ), "Impossible d'enregistrer ce motif.");
    };

    const updateComment = async (patient, billingEvent, comment) => {
        if (!canEdit(patient, billingEvent)) return;
        const info = parseChronicDescription(billingEvent.description);
        if ((info.comment || "") === String(comment || "").trim()) return;
        await runOnLine(patient, billingEvent, () => putLine(
            billingEvent,
            formatChronicDescription(info.productInfo, info.reason, info.medicamentId, comment)
        ), "Impossible d'enregistrer ce commentaire.");
    };

    const confirmBill = async () => {
        const { patient, event: billingEvent } = billCandidate || {};
        if (!canEdit(patient, billingEvent)) return;
        const info = parseChronicDescription(billingEvent.description);
        const medicamentId = info.medicamentId || await resolveMedicamentId(patient.patientId, info.productInfo);
        if (!medicamentId) {
            onError("Impossible de retrouver le médicament rattaché à cette ligne.");
            setBillCandidate(null);
            return;
        }
        await runOnLine(patient, billingEvent, async () => {
            const response = await API.patch(`/medicaments/${medicamentId}`, {
                billingValidated: true,
                billingUsername: user?.username || user?.email || null,
            }, apiConfig);
            const billedInfo = markChronicProductInfoBilled(info.productInfo, response.data || {});
            await putLine(billingEvent, formatChronicDescription(billedInfo, info.reason, medicamentId, info.comment));
            setBillCandidate(null);
        }, "Impossible de marquer ce produit comme facturé.");
    };

    const confirmDelete = async () => {
        const { patient, event: billingEvent } = deleteCandidate || {};
        if (!canEdit(patient, billingEvent)) return;
        await runOnLine(patient, billingEvent, async () => {
            await API.delete(`/administration/billing-events/${billingEvent.id}`, apiConfig);
            setDeleteCandidate(null);
        }, "Impossible de retirer ce produit de la facturation.");
    };

    return {
        savingId, updateReason, updateComment,
        billCandidate, setBillCandidate, confirmBill,
        deleteCandidate, setDeleteCandidate, confirmDelete,
        historyPatient, setHistoryPatient,
    };
}
